// Teller Online Files
import { PublicHelpTag } from 'apps/public-portal/src/app/shared/help/help-tags';
import { helpTitle } from 'apps/public-portal/src/app/shared/help/help-titles';

// Teller Online Library Files
import { HelpArticle, HelpConditional } from 'teller-online-libraries/help';

export const LINKED_ACCOUNTS_HELP_ARTICLES: HelpArticle[] = [
    new HelpArticle({
        tag: PublicHelpTag.LinkedAccounts,
        order: 20,
        conditions: [HelpConditional.Users, HelpConditional.LinkedAccounts],
        bookmark: 'what-are-linked-accounts',
        title: helpTitle('what-are-linked-accounts'),
        body: '<p>|term:Plural| are things you pay regularly that you have added to your sign-in. '
            + 'Once one is on your list, you can see what you owe on it, see what is open, and '
            + 'pay it without searching for it again.</p>'
            + '<p>To see yours, choose <b>|term:label-plural|</b> in the menu on the left. It is '
            + 'shown on every page except your profile settings pages. On a phone, click your '
            + 'avatar and choose it from the menu.</p>'
            + '<p>You need to be signed in to add or view them.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.LinkedAccounts,
        order: 21,
        conditions: [HelpConditional.Users, HelpConditional.LinkedAccounts],
        bookmark: 'how-do-i-add-a-linked-account',
        title: helpTitle('how-do-i-add-a-linked-account'),
        body: '<ol>'
            + '<li>Open <b>|term:label-plural|</b> and click <b>Add '
            + '|term:label-singular|</b>.</li>'
            + '<li>Choose the <b>Type</b>, then fill in the details shown. Each field gives an '
            + 'example of what to enter. If you aren\'t sure where to find the information, please '
            + 'contact us.</li>'
            + '<li>Click <b>Search</b>. Check that the details are correct, add a nickname if you '
            + 'want one, and click <b>Add |term:label-singular|</b>.</li>'
            + '</ol>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.LinkedAccounts,
        order: 22,
        conditions: [HelpConditional.Users, HelpConditional.LinkedAccounts],
        bookmark: 'linked-account-nickname',
        title: helpTitle('linked-account-nickname'),
        body: '<p>A nickname is a name you choose so you can recognize one of your |term:plural| '
            + 'at a glance. It is optional. If you leave it blank, its type is used instead.</p>'
            + '<p>To add or change one, click the more-actions button :more_vert:, choose '
            + '<b>Manage</b>, click the edit button :edit: beside the name, and click '
            + '<b>Save</b>.</p>'
            + '<p>Search covers nicknames, types, account numbers and descriptions, so you can '
            + 'find one by any of them.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.LinkedAccounts,
        order: 23,
        conditions: [HelpConditional.Users, HelpConditional.LinkedAccounts],
        bookmark: 'linked-account-balance-status',
        title: helpTitle('linked-account-balance-status'),
        body: '<ul><li>'
            + '<b>Balance</b> is the amount last reported as owed. A badge beside it shows where '
            + 'it stands: <b>Paid</b> (nothing owed), <b>Due</b> (an amount is owed), <b>Past '
            + 'due</b> (the next due date has passed) or <b>Closed</b> (it can no longer be '
            + 'paid).</li>'
            + '<li>'
            + '<b>Open items</b> are the unpaid items, such as bills or invoices. The card shows '
            + 'how many there are and when the next one is due. A paid one shows its last payment '
            + 'instead.</li>'
            + '<li>'
            + '<b>Updated</b> shows when the figures were last confirmed.</li></ul>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.LinkedAccounts,
        order: 24,
        conditions: [HelpConditional.Users, HelpConditional.LinkedAccounts],
        bookmark: 'how-do-i-refresh-a-linked-account',
        title: helpTitle('how-do-i-refresh-a-linked-account'),
        body: '<p>The figures are saved from the last time they were checked. They do not update '
            + 'by themselves when you sign in. Click the refresh button :refresh: to check '
            + 'again. The <b>Updated</b> time shows when it last succeeded.</p>'
            + '<p>If the latest figures can\'t be retrieved, the last known figures stay on screen '
            + 'with the warning <b>Failed to refresh data</b>. Try again later. If it can no '
            + 'longer be found, the warning reads <b>No longer available</b>.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.LinkedAccounts,
        order: 25,
        conditions: [HelpConditional.Users, HelpConditional.LinkedAccounts],
        bookmark: 'how-do-i-pay-a-linked-account',
        title: helpTitle('how-do-i-pay-a-linked-account'),
        body: '<ul><li>'
            + '<b>Pay the whole balance:</b> click <b>Pay Balance</b>. Everything due on it is '
            + 'added to your cart and you go straight to checkout. If your cart already has '
            + 'items, you are asked whether to continue first.</li>'
            + '<li>'
            + '<b>Pay several at once:</b> click the select button :playlist_add_check:, tick '
            + 'the ones to pay, then click <b>Pay Selected</b>. Any with nothing to pay are '
            + 'skipped. On a touch screen you can also press and hold one to start '
            + 'selecting.</li>'
            + '<li>'
            + '<b>Pay only some items:</b> click the more-actions button :more_vert:, choose '
            + '<b>View Items</b>, and add just the items you want to your cart.</li>'
            + '</ul>'
            + '<p>You can\'t pay one that is closed, has nothing open, or is no longer available '
            + 'online.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.LinkedAccounts,
        order: 26,
        conditions: [HelpConditional.Users, HelpConditional.LinkedAccounts],
        bookmark: 'how-many-linked-accounts',
        title: helpTitle('how-many-linked-accounts'),
        body: '<p>There is a limit to how many |term:plural| you can have. If you reach it, you '
            + 'are asked to remove some before you can add another.</p>'
            + '<p>To remove one, click the more-actions button :more_vert: on it and choose '
            + '<b>Remove</b>. To remove several, click the select button :playlist_add_check:, '
            + 'tick them, then click <b>Remove</b>. You are asked to confirm.</p>'
            + '<p>Removing one only takes it off your list. It does not change what you owe or '
            + 'any payment you have made, and you can add it again later.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.LinkedAccounts,
        order: 27,
        conditions: [HelpConditional.Users, HelpConditional.LinkedAccounts],
        bookmark: 'linked-account-unavailable',
        title: helpTitle('linked-account-unavailable'),
        body: '<p>This type can no longer be used online, so it can\'t be refreshed, paid or '
            + 'viewed. You can still change its nickname or remove it. There is no promise it '
            + 'will come back.</p>'
            + '<p>This is different from the warnings <b>Failed to refresh data</b> (the latest '
            + 'figures couldn\'t be retrieved, so try again later) and <b>No longer available</b> '
            + '(it wasn\'t found; the figures shown are the last known).</p>'
            + '<p>If a message says one of them <b>is no longer on your list</b>, it was removed '
            + 'from another device or by an administrator. You can add it again.</p>'
    })
];
